import React, { useState, useEffect } from 'react';
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader
} from 'react-calendar-timeline';
import 'react-calendar-timeline/lib/Timeline.css';
import moment from 'moment';
import { supabase } from '../lib/supabase';
import { Project } from '../types';

interface TimelineItem {
  id: string;
  group: string;
  title: string;
  start_time: number;
  end_time: number;
  itemProps?: {
    style?: React.CSSProperties;
  };
}

interface TimelineGroup {
  id: string;
  title: string;
  color?: string;
  projectCount: number;
}

const monthToNumber: { [key: string]: number } = {
  'January': 0,
  'February': 1,
  'March': 2,
  'April': 3,
  'May': 4,
  'June': 5,
  'July': 6,
  'August': 7,
  'September': 8,
  'October': 9,
  'November': 10,
  'December': 11
};

const parseDuration = (durationString: string): number => {
  const match = durationString.match(/(\d+)/);
  return match ? parseInt(match[0], 10) : 3; // Default to 3 months if parsing fails
};

// Default colors if customer doesn't have one assigned
const defaultColors = [
  '#3B82F6', // blue-500
  '#EF4444', // red-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#F97316', // orange-500
  '#6366F1', // indigo-500
];

export function ProjectTimeline() {
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [groups, setGroups] = useState<TimelineGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          project_name,
          month,
          year,
          customer:customers (
            id,
            name,
            color
          ),
          duration:durations (
            id,
            name
          )
        `)
        .order('year');

      if (projectsError) throw projectsError;

      // Create a map of customers and count their projects
      const customerMap = new Map<string, TimelineGroup>();
      let colorIndex = 0;

      projectsData.forEach((project: any) => {
        if (!project.customer) return;

        const customerId = project.customer.id;
        if (!customerMap.has(customerId)) {
          customerMap.set(customerId, {
            id: customerId,
            title: project.customer.name,
            color: project.customer.color || defaultColors[colorIndex++ % defaultColors.length],
            projectCount: 0
          });
        }
        
        const customerInfo = customerMap.get(customerId)!;
        customerInfo.projectCount++;
        customerMap.set(customerId, customerInfo);
      });

      const timelineItems: TimelineItem[] = [];

      projectsData.forEach((project: any) => {
        if (!project.customer) return;

        const customerInfo = customerMap.get(project.customer.id);
        if (!customerInfo) return;

        if (project.year && project.month && project.duration) {
          const startYear = parseInt(project.year);
          const startMonth = monthToNumber[project.month];
          const durationMonths = parseDuration(project.duration.name);

          const startTime = moment([startYear, startMonth]).valueOf();
          const endTime = moment([startYear, startMonth])
            .add(durationMonths, 'months')
            .valueOf();

          timelineItems.push({
            id: project.id,
            group: project.customer.id,
            title: project.project_name,
            start_time: startTime,
            end_time: endTime,
            itemProps: {
              style: {
                backgroundColor: customerInfo.color,
                color: 'white',
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              }
            }
          });
        }
      });

      // Sort groups by project count in descending order
      const sortedGroups = Array.from(customerMap.values())
        .sort((a, b) => b.projectCount - a.projectCount);

      setGroups(sortedGroups);
      setItems(timelineItems);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading timeline...</div>
      </div>
    );
  }

  const minTime = moment().subtract(5, 'year').startOf('year').valueOf();
  const maxTime = moment().add(2, 'year').endOf('year').valueOf();

  // Calculate the default visible timeframe (1 year centered on current date)
  const defaultTimeStart = moment().startOf('year').valueOf();
  const defaultTimeEnd = moment().endOf('year').valueOf();

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Project Timeline</h1>
          <div className="h-[calc(100vh-200px)] overflow-auto relative">
            <Timeline
              groups={groups}
              items={items}
              defaultTimeStart={defaultTimeStart}
              defaultTimeEnd={defaultTimeEnd}
              minZoom={1000 * 60 * 60 * 24 * 30} // 30 days minimum zoom
              maxZoom={1000 * 60 * 60 * 24 * 365} // 1 year maximum zoom
              canResize={false}
              canMove={false}
              stackItems
              itemHeightRatio={0.8}
              lineHeight={50}
              sidebarWidth={200}
              minTime={minTime}
              maxTime={maxTime}
              timeSteps={{
                second: 1,
                minute: 1,
                hour: 1,
                day: 1,
                month: 1,
                year: 1
              }}
              traditionalZoom={true}
            >
              <TimelineHeaders className="sticky" style={{ position: 'sticky', top: 0, zIndex: 2000 }}>
                <SidebarHeader>
                  {({ getRootProps }) => {
                    return (
                      <div {...getRootProps()} className="p-3 border-b font-medium text-gray-700 bg-gray-50">
                        Customer ({groups.length})
                      </div>
                    );
                  }}
                </SidebarHeader>
                <DateHeader unit="primaryHeader" className="sticky" />
                <DateHeader />
              </TimelineHeaders>
            </Timeline>
          </div>
        </div>
      </div>
    </div>
  );
}